import {
  Checkbox,
  ChevronRightIcon,
  FlexBox,
  JoonUIColor,
  SPACING,
  Switch,
  Typography,
} from "@joonapp/web-shared"
import { useTranslation } from "react-i18next"
import { create } from "zustand"

import { useRecurrance } from "./useRecurrance"
import { CloseButton } from "../../../../components/closeButton/CloseButton"
import { SelectRow } from "../../../../components/selectRow/SelectRow"
import { SelectRowContainer } from "../../../../components/selectRowContainer/SelectRowContainer"
import SlideUpModal from "../../../../components/slideUpModal/SlideUpModal"
import {
  RECURRENCE_OPTIONS,
  RecurrenceOption,
  REPETITIONS,
} from "../../../../constants"
import { useAddEditQuestStore } from "../useAddEditQuestStore"

interface RecurrenceModalStoreProps {
  isOpen: boolean
  onOpen: () => void
  onClose: () => void
}

export const useRecurrenceModalStore = create<RecurrenceModalStoreProps>(
  (set) => ({
    isOpen: false,
    onOpen: () => set({ isOpen: true }),
    onClose: () => set({ isOpen: false }),
  })
)

export const RecurrenceModal = () => {
  const { isOpen, onClose } = useRecurrenceModalStore()
  const {
    repetition,
    setRepetition,
    setRepeatsFrequency,
    setIsOngoing,
    selectedQuest,
  } = useAddEditQuestStore()
  const { selectedRecurrenceOption, setSelectedReptition, getLabel } =
    useRecurrance()
  const { t } = useTranslation()

  const isEditing = !!selectedQuest
  const checked = !repetition.every((day) => !day)

  const onToggleRepeatQuest = () => {
    if (checked) {
      setRepetition(REPETITIONS.noDays)
      setRepeatsFrequency(null)
    } else {
      setRepetition(REPETITIONS.daily)
      setRepeatsFrequency(1)
      setIsOngoing(false)
    }
  }

  return (
    <SlideUpModal
      isOpen={isOpen}
      onClose={onClose}
      zIndex={1000}
      backgroundType="overlay"
      size="fit-content"
    >
      <FlexBox
        direction="column"
        gap={SPACING.space4}
        style={{
          position: "relative",
          backgroundColor: JoonUIColor.background.xlightGray,
          borderRadius: SPACING.space2,
          padding: SPACING.space4,
        }}
      >
        <CloseButton onClick={onClose} />
        <Typography variant="bodyBold">{t("QUEST_SET_RECURRENCE")}</Typography>

        {!isEditing && (
          <Typography variant="bodyXSmall">
            {t("QUEST_RECURRENCE_HELP")}
          </Typography>
        )}

        <FlexBox
          direction="column"
          gap={SPACING.space2}
          style={{
            padding: SPACING.space3,
            borderRadius: SPACING.space2,
            background: JoonUIColor.background.primaryNeutral,
            opacity: isEditing ? 0.5 : 1,
            pointerEvents: isEditing ? "none" : "auto",
            fontStyle: isEditing ? "italic" : "normal",
          }}
        >
          <FlexBox
            direction="row"
            gap={SPACING.space2}
            align="center"
            justify="space-between"
          >
            <Typography variant="bodySmallBold">
              {t("QUEST_REPEAT_QUEST")}
            </Typography>
            <Switch
              checked={checked}
              onChange={onToggleRepeatQuest}
              name="repeat-quest"
            />
          </FlexBox>
          <Typography variant="bodyXSmall" color={JoonUIColor.text.secondary}>
            {t("QUEST_REPEAT_HELP")}
          </Typography>
        </FlexBox>

        {checked && (
          <SelectRowContainer>
            {RECURRENCE_OPTIONS.map((option, index) => (
              <SelectRow
                key={index}
                onClick={() =>
                  setSelectedReptition(option.value as RecurrenceOption)
                }
                hideBorder
                showBorderBottom={option.value !== RecurrenceOption.CUSTOM}
              >
                <Typography variant="bodySmallBold" textAlign="left">
                  {getLabel(option)}
                </Typography>

                {index === RECURRENCE_OPTIONS.length - 1 &&
                option.value === RecurrenceOption.CUSTOM ? (
                  <ChevronRightIcon
                    color={JoonUIColor.icon.neutral}
                    size={24}
                  />
                ) : (
                  <Checkbox
                    selected={option.value === selectedRecurrenceOption}
                    inputType="radio"
                    name={option.value?.toString() || "none"}
                    onChange={() => {}}
                    hideBorder
                  />
                )}
              </SelectRow>
            ))}
          </SelectRowContainer>
        )}
      </FlexBox>
    </SlideUpModal>
  )
}
